import React, { ReactElement } from "react";
import { DataItem, DataItemHeader } from "./panel-menu.types";

const renderUniqueId = (
  previousId: string,
  index: number,
  level: number
): string => `${previousId}_${index.toString()}_${level.toString()}`;

/**
 * @param items parses React Children if `children` exists
 * @param data parses json data array if "data" or "dataSecondary" prop exists
 */

export const navItemsParser = (
  items: React.ReactNode[] | null,
  data: DataItem[] | DataItemHeader[] | null,
  closeOnClickHeaderGlobals: { isGlobal?: boolean; callback: any } = {
    isGlobal: false,
    callback: (): void => {},
  },
  isHeader: boolean = false,
  isHeaderSecondary: boolean = false,
  level: number = 0,
  previousId: string = ""
): DataItem[] => {
  let navElements: (DataItem & DataItemHeader)[] | null = null;

  if (items !== null) {
    navElements = items
      .filter((item): item is ReactElement => React.isValidElement(item))
      .filter((item) => {
        const itemType: any = item.type;
        return (
          itemType?.displayName === "MenuItem" ||
          itemType?.displayName === "NavigationItem"
        );
      })
      .map((item) => item.props);
  }

  return (navElements || data || []).map((item, index) => {
    // "label" is used only on Header Navigation Items
    // Standalone Panel Menu Items uses "title"
    const {
      children,
      label,
      iconName,
      iconBadgeProps,
      spotlight,
      closeOnClick,
      ...props
    } = item as DataItem & DataItemHeader;

    const uniqueId = renderUniqueId(previousId, index, level);

    return {
      ...props,
      id:
        props.id ||
        `menu_item${isHeader ? "_header" : ""}${
          isHeaderSecondary ? "_secondary" : ""
        }${uniqueId}`,
      title: props.title ?? label,
      _privateProps: {
        iconName,
        iconBadgeProps,
        spotlight,
        visible: true,
        ...props._privateProps,
        isTopLevelItem: level === 0,
        isHeaderSecondary,
        closeOnClick: closeOnClickHeaderGlobals.isGlobal ?? closeOnClick,
        handleCloseOnClick: closeOnClickHeaderGlobals.callback,
      },
      children: item.children
        ? navItemsParser(
            items ? React.Children.toArray(children as any) : null,
            data ? children || [] : null,
            closeOnClickHeaderGlobals,
            isHeader,
            isHeaderSecondary,
            level + 1,
            uniqueId
          )
        : undefined,
    };
  });
};
